import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import "./PublicProgram.css";
import PublicExerciseCard from "./PublicExerciseCard";
import { SlideshowModal } from "@features/programs/modals/SlideshowModal";
import { useTranslation } from "react-i18next";
import RegistrationBanner from "@features/public-access/modals/RegistrationBanner";
import RegistrationModal from "@features/public-access/modals/RegistrationModal";
import http from "@shared/api/http";

function setExerciseLayout(data, columns) {
  let content_block = [];
  let content = null;
  for (let i = 0; i < data?.length; i += columns) {
    content = (
      <Row className="program-exercise-row" key={i}>
        <Col md={12} lg={6} xxl={4}>
          {data[i]}
        </Col>
        {(() => {
          if (data[i + 1] !== undefined && columns >= 2) {
            return (
              <Col md={12} lg={6} xxl={4}>
                {data[i + 1]}
              </Col>
            );
          }
        })()}
        {(() => {
          if (data[i + 2] !== undefined && columns === 3) {
            return (
              <Col md={12} lg={6} xxl={4}>
                {data[i + 2]}
              </Col>
            );
          }
        })()}
      </Row>
    );
    content_block.push(content);
  }
  return content_block;
}

function getBootstrapBreakpoint() {
  var w = window.innerWidth;
  return w < 900 ? 1 : w < 1440 ? 2 : 3;
}

function PublicProgram() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [columns, setColumns] = useState(getBootstrapBreakpoint());
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [registrationError, setRegistrationError] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const { t } = useTranslation();

  // Get program data from navigation state
  const programData = location.state?.programData;

  useEffect(() => {
    const handleResize = () => {
      setColumns(getBootstrapBreakpoint());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!programData) {
    return (
      <Container className="main-container">
        <Row className="justify-content-center align-items-center min-vh-100">
          <Col xs={12} md={8} lg={6}>
            <Card className="text-center p-4">
              <Card.Body>
                <h3>{t("public-program.program-not-found")}</h3>
                <p>{t("public-program.program-not-found-description")}</p>
                <Button variant="primary" onClick={() => navigate("/public-access")}>
                  {t("public-program.back-to-access-page")}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  const startSlideshow = (exerciseIndex) => {
    setCurrentIndex(exerciseIndex);
    setShowSlideshow(true);
  };

  const handleNext = () => {
    if (currentIndex < programData.exercises.length) { // Allow going to feedback slide (+1)
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleRegistrationClick = () => {
    setShowRegistrationModal(true);
    setRegistrationError(null);
    setRegistrationSuccess(false);
  };

  const handleRegistration = async (formData) => {
    setRegistrationLoading(true);
    setRegistrationError(null);
    setRegistrationSuccess(false);

    try {
      // Deliberately kept on fetch() (not the shared http client): the
      // error branch below reads data.message from non-2xx responses via
      // response.ok; axios would throw on those statuses instead.
      const response = await fetch(
        process.env.REACT_APP_SOPHYAPP_API_URL + `/de/v2/patient`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            patient: {
              name: formData.email.split('@')[0], // Use email prefix as name
              email: formData.email,
              password: formData.password,
              password_confirmation: formData.passwordConfirmation,
              language: 'de'
            }
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        setRegistrationSuccess(true);
        // Close modal after 2 seconds
        setTimeout(() => {
          setShowRegistrationModal(false);
          setRegistrationSuccess(false);
        }, 2000);
      } else {
        setRegistrationError(data.message || t('registration-modal.error-generic', 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.'));
      }
    } catch (error) {
      setRegistrationError(t('registration-modal.error-network', 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.'));
    } finally {
      setRegistrationLoading(false);
    }
  };

  const handleCloseRegistrationModal = () => {
    setShowRegistrationModal(false);
    setRegistrationError(null);
    setRegistrationSuccess(false);
  };

  const handleSlideshowFeedback = async (feedbackLvl, feedbackText) => {
    // Check if patient is registered
    if (programData.patient_registered === false) {
      // Show registration message for unregistered users
      alert(t("public-program.feedback_requires_registration", "To submit feedback, please register for an account. This will allow you to track your progress and communicate with your therapist."));
      return;
    }

    try {
      // Gather security identifiers from navigation state
      const codeFromState = location.state?.programCode;
      const lastNameFromState = location.state?.lastName;
      const programCode = codeFromState || programData.program_code || '';
      const lastName = lastNameFromState || '';

      if (!programCode || !lastName) {
        alert(t("public-program.feedback_not_available", "Feedback submission is not available for this access. Please contact your therapist directly."));
        return;
      }

      // Public feedback endpoint (no auth, so no :token), validated by
      // program_code + last_name server-side
      const url = "/:locale/react/public/programs/feedback";
      const payload = {
        program_code: programCode,
        last_name: lastName,
        pain_level: feedbackLvl,
        feedback_text: feedbackText || ''
      };

      console.log('Sending feedback to:', url);
      console.log('Payload:', payload);

      const response = await http.post(url, payload, { headers: { 'Content-Type': 'application/json' } });

      if (response.status >= 200 && response.status < 300) {
        alert(t("public-program.feedback_submitted", "Thank you for your feedback! Your therapist will review it."));
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (error) {
      console.error("Error sending feedback:", error);
      alert(t("public-program.feedback_error", "There was an error submitting your feedback. Please try again."));
    }
  };

  const renderExercises = () => {
    return programData.exercises.map((exercise, index) => (
      <div key={exercise.id} className="exercise-card-wrapper">
        <PublicExerciseCard
          exercise={exercise}
          position={index + 1}
          onStartSlideshow={() => startSlideshow(index)}
        />
      </div>
    ));
  };

  return (
    <div className="public-program-wrapper">
      <Container fluid>
        <div className="public-program-content">
          {/* Header */}
          <Row className="public-program-header">
            <Col>
              <div className="public-program-title-section">
                <Button
                  className="back-button"
                  size="sm"
                  onClick={() => navigate("/public-access")}
                >
                  ← {t("public-program.back-to-access-page")}
                </Button>
                <h1 className="public-program-title">{programData.name}</h1>
                {programData.description && (
                  <p className="public-program-description">{programData.description}</p>
                )}
              </div>
            </Col>
          </Row>

          {/* Registration Banner - only show if patient is not registered */}
          {programData.patient_registered === false && programData.patient_email && (
            <Row>
              <Col>
                <RegistrationBanner 
                  onRegisterClick={handleRegistrationClick}
                  patientEmail={programData.patient_email}
                />
              </Col>
            </Row>
          )}

          {/* Program Info */}
          <Row className="public-program-info g-3">
            <Col md={6} lg={3}>
              <Card className="info-card h-100">
                <Card.Body>
                  <Card.Title>{t("program.target")}</Card.Title>
                  <Card.Text>{programData.goal}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="info-card h-100">
                <Card.Body>
                  <Card.Title>{t("program.frequency")}</Card.Title>
                  <Card.Text>{programData.frequency} {t("program.per-week")}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="info-card h-100">
                <Card.Body>
                  <Card.Title>{t("program.duration")}</Card.Title>
                  <Card.Text>{programData.duration} {t("program.weeks")}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="info-card h-100">
                <Card.Body>
                  <Card.Title>{t("program.exercises")}</Card.Title>
                  <Card.Text>{programData.exercises.length} {t("program.exercises")}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Therapist Info */}
          {programData.account && (
            <Row className="therapist-info mt-4">
              <Col>
                <Card className="therapist-card">
                  <Card.Body>
                    <Card.Title>{t("public-program.your-physiotherapist")}</Card.Title>
                    <div className="therapist-details">
                      <div>
                        <strong>{programData.account.clinic_name}</strong><br/>
                        {programData.account.first_name} {programData.account.last_name}
                      </div>
                      <div>
                        {programData.account.city && <span>{programData.account.city}<br/></span>}
                        {programData.account.phone && <span>{t("public-program.phone")}: {programData.account.phone}<br/></span>}
                        {programData.account.website && (
                          <a href={programData.account.website} target="_blank" rel="noopener noreferrer">
                            {t("public-program.visit-website")}
                          </a>
                        )}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Action Buttons */}
          <Row className="action-buttons mt-4">
            <Col>
              <div className="button-group">
                <Button
                  className="start-slideshow-button"
                  size="lg"
                  onClick={() => startSlideshow(0)}
                  disabled={!programData.exercises.length}

                >
                  {t("feedback.startSlideShow")}
                </Button>
              </div>
            </Col>
          </Row>

          {/* Exercises */}
          <Row className="mt-4">
            <Col>
              <h3 className="exercises-title">{t("program.your-exercises")}</h3>
              <div className="exercises-container">
                {setExerciseLayout(renderExercises(), columns)}
              </div>
            </Col>
          </Row>

          {/* Footer */}
          <Row className="public-program-footer mt-4">
            <Col>
              <div className="footer-content">
                <p>
                  {t("public-program.program-created-for-you")}
                </p>
                <p className="disclaimer-public-program">
                  <strong>{t("public-program.important")}:</strong> {t("public-program.stop-exercising")}
                </p>
              </div>
            </Col>
          </Row>
        </div>

        {/* Slideshow Modal */}
        {showSlideshow && (
          <SlideshowModal
            show={showSlideshow}
            onHide={() => setShowSlideshow(false)}
            exercises={{
              props: {
                exercise: {
                  name: programData.exercises[currentIndex]?.name || '',
                  prescription: programData.exercises[currentIndex]?.prescription || '',
                  source: {
                    video_url: programData.exercises[currentIndex]?.video_url || '',
                    steps: Array.isArray(programData.exercises[currentIndex]?.steps) 
                      ? programData.exercises[currentIndex].steps
                      : programData.exercises[currentIndex]?.steps?.split("<br />").filter(step => step.trim() !== "") || []
                  }
                }
              }
            }}
            currentIndex={currentIndex}
            onNext={handleNext}
            onPrevious={handlePrevious}
            programLength={programData.exercises.length}
            onSubmitFeedback={handleSlideshowFeedback}
          />
        )}

        {/* Registration Modal */}
        <RegistrationModal
          show={showRegistrationModal}
          onHide={handleCloseRegistrationModal}
          patientEmail={programData.patient_email}
          onRegister={handleRegistration}
          loading={registrationLoading}
          error={registrationError}
          success={registrationSuccess}
        />
      </Container>
    </div>
  );
}

export default PublicProgram; 